import { create } from 'zustand';

/**
 * Tek "bağlantı durumu" otoritesi (Faz 1 — kalıcı profesyonel liveness).
 *
 * Backend'e giden WS (session-ws) heartbeat'i bu store'u sürer:
 *  - WS açık + taze pong → 'live'
 *  - WS kapalı / pong gelmiyor (backend down/hung) → 'offline'
 *  - ilk bağlanma / yeniden bağlanma denemesi → 'connecting'
 *
 * Bütün canlı UI TEK bu sinyali okur (dağınık navigator.onLine + REST-probe +
 * sipState convergence'ı yerine). REST 502-probe'a göre çok daha HIZLI: WS
 * pong-timeout saniyeler içinde offline'a düşürür (eski yol 2 başarısız probe =
 * 20-60s sürüyordu).
 *
 * serverTimeOffset: pong'daki sunucu zamanı ile yerel saat farkı. Süre
 * hesapları bununla yapılır → tarayıcı saati kayması KPI'yı bozmaz.
 *
 * NOT: Bu sinyal BACKEND ulaşılabilirliğidir — telefon (3CX/SIP) sağlığı AYRI
 * (StatusPill + useSipHealth). Backend up + PBX down ise süreler akmaya devam
 * eder (veri DB'de doğru), sadece telefon uyarısı çıkar.
 */
export type ConnectionStatus = 'connecting' | 'live' | 'offline';

interface ConnectionState {
  status: ConnectionStatus;
  /** Son pong (heartbeat) zamanı, ms epoch. */
  lastHeartbeatAt: number | null;
  /** serverNow − clientNow (ms). Süre interpolasyonunda kullanılır. */
  serverTimeOffset: number;
  /** En az bir kez 'live' olduk mu — ilk-bağlanma ile kopuşu ayırt etmek için. */
  everLive: boolean;
}

export const useConnection = create<ConnectionState>(() => ({
  status: 'connecting',
  lastHeartbeatAt: null,
  serverTimeOffset: 0,
  everLive: false,
}));

// ─── WS istemcisinin (React dışı) çağırdığı action'lar ───────────────────────

/** Socket açıldı — henüz pong yok, 'connecting' (taze pong gelince 'live'). */
export function connOnOpen(): void {
  useConnection.setState({ status: 'connecting' });
}

/** pong geldi — backend canlı + sunucu saati senkron. */
export function connOnHeartbeat(serverTsMs: number): void {
  const now = Date.now();
  const offset = Number.isFinite(serverTsMs) ? serverTsMs - now : 0;
  useConnection.setState({ status: 'live', lastHeartbeatAt: now, serverTimeOffset: offset, everLive: true });
}

/** Socket kapandı / pong-timeout / bağlanma başarısız → offline. (Açılış
 *  penceresinde default 'connecting' geçerli; offline yalnızca gerçek kapanış/
 *  başarısızlıkta — başlangıç flaşı olmaz çünkü onclose ancak deneme bitince fırlar.) */
export function connOnDown(): void {
  useConnection.setState({ status: 'offline' });
}

/** Logout / temizlik — başlangıç durumuna dön. */
export function connReset(): void {
  useConnection.setState({ status: 'connecting', lastHeartbeatAt: null, serverTimeOffset: 0, everLive: false });
}

/** Sunucu-senkron şimdiki zaman (ms). Süre hesaplarında Date.now() yerine bunu kullan. */
export function serverNow(): number {
  return Date.now() + useConnection.getState().serverTimeOffset;
}

/**
 * Canlı query'leri gate'lemek için: backend offline'ken poll'u durdur (502 hammer
 * + log spam + pil tüketimi olmasın). React Query `enabled` ile kullan:
 *   enabled: useBackendOnline() && <mevcut koşul>
 * Reconnect olunca ('offline' → 'live') query'ler otomatik yeniden etkinleşir.
 */
export const useBackendOnline = (): boolean => useConnection((s) => s.status !== 'offline');
